import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getSubmissions } from '../services/api';
import StatusBadge from '../components/StatusBadge';
import { Plus, FileText, Clock, CheckCircle, XCircle, Inbox, Zap } from 'lucide-react';

const AuthorDashboard = () => {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getSubmissions(user?.id)
      .then(data => { setSubmissions(data || []); setLoading(false); })
      .catch(() => { setSubmissions([]); setLoading(false); });
  }, [user]);

  const stats = {
    total: submissions.length,
    // Under review = any stage before final decision
    under_review: submissions.filter(s => ['submitted', 'editor_review', 'reviewer_review'].includes(s.status)).length,
    // Accepted = published
    accepted: submissions.filter(s => s.status === 'published').length,
    rejected: submissions.filter(s => s.status === 'rejected').length,
  };

  return (
    <div className="bg-[#F5F7FA] min-h-screen" data-testid="author-dashboard">
      <div className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="section-overline mb-1">Author Portal</p>
              <h1 className="font-serif text-3xl font-semibold text-[#1F1F1F]">
                Welcome, {user?.name?.split(' ').pop()}
              </h1>
              <p className="text-[#6B7280] text-sm mt-1">{user?.affiliation || 'MAH Quantum Research Institute'}</p>
            </div>
            <Link
              to="/submit"
              className="flex items-center gap-2 px-5 py-2.5 bg-[#1A73E8] text-white text-sm font-medium hover:bg-[#174EA6] transition-colors flex-shrink-0"
              data-testid="new-submission-btn"
            >
              <Plus size={15} /> New Submission
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Submissions', value: stats.total, icon: <FileText size={18} />, color: 'text-[#1A73E8]', bg: 'bg-blue-50' },
            { label: 'Under Review', value: stats.under_review, icon: <Clock size={18} />, color: 'text-amber-600', bg: 'bg-amber-50' },
            { label: 'Accepted', value: stats.accepted, icon: <CheckCircle size={18} />, color: 'text-green-600', bg: 'bg-green-50' },
            { label: 'Rejected', value: stats.rejected, icon: <XCircle size={18} />, color: 'text-red-500', bg: 'bg-red-50' },
          ].map(stat => (
            <div key={stat.label} className="bg-white border border-[#E5E7EB] p-5" data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
              <div className={`w-9 h-9 ${stat.bg} flex items-center justify-center mb-3 ${stat.color}`}>
                {stat.icon}
              </div>
              <p className="text-2xl font-serif font-bold text-[#1F1F1F]">{stat.value}</p>
              <p className="text-xs text-[#6B7280] mt-1">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white border border-[#E5E7EB]">
              <div className="px-5 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
                <h2 className="font-semibold text-[#1F1F1F] text-base">My Submissions</h2>
                <span className="text-xs text-[#6B7280]">{submissions.length} papers</span>
              </div>
              {loading ? (
                <div className="p-8 text-center text-[#9CA3AF] text-sm">Loading submissions...</div>
              ) : submissions.length === 0 ? (
                <div className="p-12 text-center">
                  <Inbox size={32} className="text-[#E5E7EB] mx-auto mb-3" />
                  <p className="text-[#6B7280] text-sm mb-3">No submissions yet.</p>
                  <Link to="/submit" className="text-sm text-[#1A73E8] hover:underline">Submit your first paper</Link>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="academic-table" data-testid="submissions-table">
                    <thead>
                      <tr>
                        <th>Paper</th>
                        <th>Journal</th>
                        <th>Status</th>
                        <th>Submitted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {submissions.map(sub => (
                        <tr key={sub.id} data-testid={`submission-row-${sub.id}`}>
                          <td className="max-w-[220px]">
                            <p className="font-medium text-[#1F1F1F] text-sm leading-snug line-clamp-2">{sub.title}</p>
                            <p className="text-xs text-[#9CA3AF] mt-0.5 font-mono">{sub.id}</p>
                          </td>
                          <td>
                            <span className="text-xs text-[#6B7280]">
                              {mockJournalShortId(sub.journalTitle)}
                            </span>
                          </td>
                          <td><StatusBadge status={sub.status} /></td>
                          <td className="text-xs text-[#6B7280]">{sub.submittedAt}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-5">
            <div className="bg-white border border-[#E5E7EB]">
              <div className="px-5 py-3 bg-[#F5F7FA] border-b border-[#E5E7EB]">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Quick Actions</h3>
              </div>
              <div className="p-4 space-y-2">
                {[
                  { label: 'Submit New Paper', path: '/submit', primary: true },
                  { label: 'Browse Journals', path: '/journals', primary: false },
                  { label: 'View Editorial Board', path: '/editorial-board', primary: false },
                ].map(action => (
                  <Link key={action.label} to={action.path}
                    className={`block w-full text-center py-2.5 text-sm font-medium transition-colors ${
                      action.primary
                        ? 'bg-[#1A73E8] text-white hover:bg-[#174EA6]'
                        : 'border border-[#E5E7EB] text-[#374151] hover:bg-[#F5F7FA]'
                    }`}
                    data-testid={`quick-action-${action.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {action.label}
                  </Link>
                ))}
              </div>
            </div>

            <div className="bg-white border border-[#E5E7EB]">
              <div className="px-5 py-3 bg-[#F5F7FA] border-b border-[#E5E7EB] flex items-center gap-2">
                <Zap size={13} className="text-[#1A73E8]" />
                <h3 className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">AI Research Tools</h3>
              </div>
              <div className="p-4 space-y-3">
                {[
                  { title: 'AI Abstract Summarizer', desc: 'Generate structured abstracts from full text' },
                  { title: 'AI Reviewer Matching', desc: 'Find relevant peer reviewers for your paper' },
                  { title: 'AI Paper Quality Check', desc: 'Pre-submission quality and originality analysis' },
                ].map(tool => (
                  <div key={tool.title} className="border border-[#E5E7EB] p-3 bg-[#F9FAFB]">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-semibold text-[#374151]">{tool.title}</p>
                      <span className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-600 border border-blue-200">Coming Soon</span>
                    </div>
                    <p className="text-xs text-[#9CA3AF]">{tool.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-[#E5E7EB] p-5">
              <div className="flex items-center gap-3 mb-3 pb-3 border-b border-[#E5E7EB]">
                <div className="w-10 h-10 bg-[#1A73E8] flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">{user?.name?.charAt(0)}</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#1F1F1F]">{user?.name}</p>
                  <p className="text-xs text-[#6B7280]">Author</p>
                </div>
              </div>
              <p className="text-xs text-[#6B7280] leading-relaxed">{user?.email}</p>
              {user?.affiliation && <p className="text-xs text-[#4B5563] mt-1">{user.affiliation}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mockJournalShortId = (title) => {
  if (!title) return '—';
  const map = {
    'Journal of Quantum Computing & Information Systems': 'JQCIS',
    'Journal of Advanced Materials Science': 'JAMS',
    'Journal of Biomedical & Molecular Innovations': 'JBMI',
    'Journal of Artificial Intelligence & Applications': 'JAAI',
  };
  return map[title] || title.substring(0, 6);
};

export default AuthorDashboard;
